/**
 * Booking API service layer.
 * 
 * Provides typed API methods for all booking-related operations.
 * All endpoints follow the /api/v1 base path configured in the axios client.
 */

import api from '@/lib/api';
import type { 
  DoctorSearchParams, 
  DoctorSearchResult,
  DoctorSearchResponse,
  DoctorDetailResponse,
  AvailabilityResponse,
  ReimbursementEstimation,
  WaitlistRequest,
  WaitlistEntry,
  Appointment,
  TimeSlot,
} from '@/lib/types';

// ============================================================================
// DOCTORS API
// ============================================================================

import type { SpringPage } from '@/lib/types';

/**
 * Normaliseur central de la pagination « doctors » (B2, issue #34).
 *
 * L'API GET /api/v1/doctors renvoie une page Spring Data
 * ({ content, totalElements, totalPages, pageNumber }) et NON
 * { doctors, total } : c'est ce désalignement qui faisait afficher
 * « 0 praticien trouvé » à l'étape 2 du tunnel alors que l'API répondait 200.
 *
 * Accepte les deux formes (page Spring ou payload legacy {doctors,total})
 * pour ne casser aucun appelant.
 */
export function normalizeDoctorsPage(
  raw: SpringPage<DoctorSearchResult> & { doctors?: DoctorSearchResult[]; total?: number }
): DoctorSearchResponse {
  const doctors = Array.isArray(raw?.content) ? raw.content : (raw?.doctors ?? []);
  const total = typeof raw?.totalElements === 'number' ? raw.totalElements : (raw?.total ?? doctors.length);
  const size = typeof raw?.size === 'number' ? raw.size : (doctors.length || 20);
  const page = typeof raw?.pageNumber === 'number' ? raw.pageNumber : (typeof raw?.page === 'number' ? raw.page : 0);
  const totalPages =
    typeof raw?.totalPages === 'number' ? raw.totalPages : (size > 0 ? Math.max(1, Math.ceil(total / size)) : 1);
  return { doctors, total, page, size, totalPages };
}

interface SearchDoctorsResponse extends Partial<DoctorSearchResponse> {
  content?: DoctorSearchResult[];
  totalElements?: number;
  pageNumber?: number;
}

interface GetNextAvailabilityResponse {
  nextAvailableAt: string;
  channel: 'PHYSICAL' | 'TELECONSULTATION';
  actTypeName: string;
}

/** Contrat reel de GET /doctors/{id} (fiche praticien, B3). */
export interface DoctorApiDetail {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  specialty: string;
  specialties: string[];
  subSpecialty: string | null;
  languages: string[];
  city: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string | null;
  phoneNumber: string | null;
  sector: 'SECTOR1' | 'SECTOR2' | 'OPTAM' | 'NON_CONVENTIONNE';
  fees: {
    sector1Fee: number | null;
    sector2Fee: number | null;
    optamFee: number | null;
    currency: string;
  };
  consultationModes: { physical: boolean; teleconsultation: boolean };
  reviews: {
    averageRating: number | null;
    totalReviews: number;
    ratingDistribution5: number;
    ratingDistribution4: number;
    ratingDistribution3: number;
    ratingDistribution2: number;
    ratingDistribution1: number;
  };
  availabilities: {
    availableDays: string[];
    earliestAvailableDate: string | null;
    latestAvailableDate: string | null;
    hasWeekendAvailability: boolean;
  };
  bookableActTypes: Array<{
    id: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    price: number;
  }>;
  nextAvailableAt: string | null;
  validatedAt: string | null;
  createdAt: string;
}

/**
 * Traduit le contrat reel de /doctors/{id} vers DoctorDetailResponse, la
 * forme attendue par le tunnel de reservation (BookingProvider, Step3..7).
 *
 * <p><b>Pourquoi cette fonction est le SEUL point d'entrée autorisé :</b> le contrat du
 * serveur et celui du tunnel diffèrent sur trois champs, et un appelant qui pose la réponse
 * brute dans le contexte casse le rendu :
 * <ul>
 *   <li>{@code consultationModes} : objet {@code {physical, teleconsultation}} côté serveur,
 *       tableau {@code ['PHYSICAL']} côté tunnel. Un {@code .includes()} sur l'objet lève
 *       {@code consultationModes.includes is not a function} — écran d'erreur.</li>
 *   <li>{@code address} : {@code addressLine1} côté serveur, {@code address} côté tunnel.</li>
 *   <li>{@code actTypes} : {@code bookableActTypes} côté serveur.</li>
 * </ul>
 * Le type de retour de {@code doctorsApi.getDetails} promettait {@code DoctorDetailResponse}
 * alors que l'API renvoie {@code DoctorApiDetail} : TypeScript validait donc des accès qui
 * n'existent pas à l'exécution. Le typage est désormais honnête et le mapping obligatoire.
 */
export function toBookingDoctor(detail: DoctorApiDetail): DoctorDetailResponse {
  const physicalFee =
    detail.sector === 'SECTOR1'
      ? detail.fees.sector1Fee
      : detail.sector === 'OPTAM'
        ? (detail.fees.optamFee ?? detail.fees.sector2Fee)
        : detail.fees.sector2Fee;

  // Le serveur ne renvoie pas toujours `consultationModes` (praticien ancien, réponse
  // partielle) : sans garde, `.physical` sur `undefined` lève aussi.
  const modes = detail.consultationModes ?? {
    physical: false,
    teleconsultation: false,
  };

  return {
    id: detail.id,
    displayName: detail.displayName,
    firstName: detail.firstName,
    lastName: detail.lastName,
    specialty: detail.specialty,
    subSpecialty: detail.subSpecialty ?? undefined,
    city: detail.city,
    address: detail.addressLine1,
    postalCode: detail.postalCode,
    sector: detail.sector,
    teleconsultationEnabled: modes.teleconsultation,
    acceptingPatients: true,
    status: 'ACTIVE',
    languages: detail.languages ?? [],
    rating: detail.reviews?.averageRating ?? undefined,
    reviewCount: detail.reviews?.totalReviews ?? 0,
    consultationModes: (['PHYSICAL', 'TELECONSULTATION'] as const).filter((mode) =>
      mode === 'PHYSICAL' ? modes.physical : modes.teleconsultation
    ),
    fees: {
      physical: physicalFee ?? undefined,
      teleconsultation: (detail.bookableActTypes ?? []).find((a) =>
        a.name.toLowerCase().includes('teleconsultation')
      )?.price,
    },
    actTypes: (detail.bookableActTypes ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      price: a.price,
      durationMinutes: a.durationMinutes,
    })),
    rppsNumber: '',
  };
}

/**
 * Normalise une fiche praticien déjà convertie en {@code DoctorDetailResponse}.
 *
 * <p>Filet de sécurité pour les objets qui auraient transité par {@code localStorage} d'une
 * version antérieure du front (où {@code consultationModes} pouvait être un objet, ou absent).
 * La donnée de stockage survit aux versions du code : on ne peut pas la supposer valide.
 */
export function normalizeBookingDoctor(
  doctor: DoctorDetailResponse | null | undefined
): DoctorDetailResponse | null {
  if (!doctor) return null;

  const raw = doctor as unknown as {
    consultationModes?: unknown;
    addressLine1?: string;
    bookableActTypes?: DoctorDetailResponse['actTypes'];
  };

  const modes = Array.isArray(raw.consultationModes)
    ? (raw.consultationModes as ('PHYSICAL' | 'TELECONSULTATION')[])
    : typeof raw.consultationModes === 'object' && raw.consultationModes !== null
      ? (['PHYSICAL', 'TELECONSULTATION'] as const).filter((mode) =>
          mode === 'PHYSICAL'
            ? Boolean((raw.consultationModes as { physical?: boolean }).physical)
            : Boolean((raw.consultationModes as { teleconsultation?: boolean }).teleconsultation)
        )
      : [];

  return {
    ...doctor,
    consultationModes: modes,
    address: doctor.address ?? raw.addressLine1 ?? '',
    actTypes: Array.isArray(doctor.actTypes) ? doctor.actTypes : (raw.bookableActTypes ?? []),
    languages: Array.isArray(doctor.languages) ? doctor.languages : [],
    fees: doctor.fees ?? {},
  };
}

export const doctorsApi = {
  /**
   * Search doctors by various criteria
   * GET /api/v1/doctors
   *
   * Retourne la payload normalisée ({ doctors, total, ... }) quel que soit
   * le format de la page recue.
   */
  search: async (params: DoctorSearchParams) => {
    // Le contrat de `DoctorSearchParams` porte `term` (nom historique du front),
    // alors que l'API attend `q`. Sans cette traduction, `term` partait en
    // `?term=Deheb` : un parametre inconnu, silencieusement ignore par le
    // serveur, donc une recherche sans filtre de texte. Verifie en production :
    // l'etape 2 listait tous les praticiens quel que soit le terme saisi.
    //
    // `teleconsultation` (booleen du front) devient `channel=TELECONSULTATION` :
    // l'API filtre par canal, pas par booleen.
    const query: Record<string, unknown> = {};
    const term = (params.term ?? (params as { q?: string }).q ?? '').trim();
    if (term) query.q = term;
    if (params.specialty) query.specialty = params.specialty;
    if (params.city) query.city = params.city;
    if (params.teleconsultation) query.channel = 'TELECONSULTATION';
    if (params.acceptingPatients) query.acceptingPatients = true;
    if (typeof params.page === 'number') query.page = params.page;
    if (typeof params.size === 'number') query.size = params.size;

    const response = await api.get<SpringPage<DoctorSearchResult>>('/doctors', { params: query });
    // Le type de la réponse HTTP reste la page brute ; la normalisation est
    // appliquée par le consommateur via normalizeDoctorsPage.
    return {
      ...response,
      data: normalizeDoctorsPage(response.data as SearchDoctorsResponse & SpringPage<DoctorSearchResult>),
    };
  },
  
  /**
   * Get detailed information about a specific doctor
   * GET /api/v1/doctors/:id
   *
   * <p><b>Corrigé :</b> le type de retour annoncé était {@code DoctorDetailResponse}, donc
   * TypeScript validait {@code consultationModes.includes(...)} dans les étapes du tunnel —
   * alors que l'API renvoie {@code DoctorApiDetail}, où {@code consultationModes} est un objet
   * {@code {physical, teleconsultation}}. À l'exécution, l'appel levait
   * {@code consultationModes.includes is not a function} et l'étape 3 affichait un écran
   * d'erreur. Le typage est désormais honnête : l'appelant DOIT passer par
   * {@code toBookingDoctor}.
   */
  getDetails: (id: string) =>
    api.get<DoctorApiDetail>(`/doctors/${id}`),

  /**
   * Fiche praticien complete (contrat reel de GET /doctors/{id}) :
   * sous-specialite, langues, honoraires par secteur, modes de consultation,
   * distribution des notes, actes reservables, prochain creneau.
   */
  getFullDetails: (id: string) =>
    api.get<DoctorApiDetail>(`/doctors/${id}`),
  
  /**
   * Get availability slots for a doctor in a date range
   * GET /api/v1/doctors/:id/availabilities
   */
  /**
   * Disponibilites d'un praticien sur une plage de dates.
   *
   * <p>Issue #65 : le backend renvoie {@code {count, slots}} ou {@code slots} est une liste
   * <b>plate</b> de creneaux ({@code startAt, endAt, durationMinutes, channel}), et NON un
   * tableau de jours {@code [{date, slots[]}]} comme l'attendait le front.
   *
   * <p>Consequence en production : {@code for (const day of availabilityQuery.data.data)}
   * levait « p.data.data is not iterable » et l'etape calendrier du tunnel affichait une
   * page blanche. Le patient ne pouvait pas reserver — le coeur du produit.
   *
   * <p>On regroupe ici par date, a la frontiere entre le contrat serveur et le modele
   * d'affichage. Chaque appelant recoit la forme attendue.
   */
  /**
   * Disponibilites d'un praticien.
   *
   * <p>`actTypeId` et `channel` sont des parametres REELS du contrat
   * (`GET /api/v1/doctors/{doctorId}/availabilities?from&to&actTypeId&channel`), tous deux
   * optionnels. Ils etaient ignores : le tunnel demandait les creneaux sans dire pour quel
   * acte ni quel canal, puis filtrait cote client. Un patient qui choisissait
   * « Teleconsultation (20 min) » a l'etape 4 se voyait donc proposer des creneaux de
   * consultation cabinet, au tarif cabinet.
   */
  getAvailabilities: async (
    id: string,
    from: Date,
    to: Date,
    options?: { actTypeId?: string; channel?: string }
  ) => {
    // ATTENTION — `toISOString()` décale la plage d'un jour.
    //
    // Les dates sont construites en heure LOCALE (`new Date(year, month, 1)`), puis
    // converties en UTC par `toISOString()`. À Paris (UTC+2), le 1er octobre 00:00 devient
    // le 30 septembre 22:00. Le backend tronque au `T` : il lit donc `2026-09-30`, soit un
    // jour AVANT la date voulue. Vérifié en production : le calendrier d'octobre demandait
    // du 30/09 au 30/10 — le 31 octobre n'était jamais interrogé, et un praticien disponible
    // ce jour-là apparaissait indisponible.
    //
    // On envoie donc une date locale `YYYY-MM-DD`, sans conversion de fuseau : c'est le
    // format que le contrat documente (`yyyy-MM-dd` ou ISO-8601) et le seul qui préserve
    // le jour civil demandé par l'utilisateur.
    const localDate = (d: Date): string => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const response = await api.get<
      { count?: number; slots?: unknown[] } | unknown[]
    >(`/doctors/${id}/availabilities`, {
      params: {
        from: localDate(from),
        to: localDate(to),
        ...(options?.actTypeId ? { actTypeId: options.actTypeId } : {}),
        ...(options?.channel ? { channel: options.channel } : {}),
      },
    });

    const raw = response.data;
    const slots: Array<Record<string, unknown>> = Array.isArray(raw)
      ? (raw as Array<Record<string, unknown>>)
      : ((raw?.slots as Array<Record<string, unknown>>) ?? []);

    // Regroupement par jour. La cle est la partie date de `startAt` (format local, sans
    // conversion de fuseau : un creneau a 09:00 doit rester le 2 novembre).
    const byDate = new Map<string, Array<Record<string, unknown>>>();
    for (const slot of slots) {
      const startAt = String(slot.startAt ?? '');
      const date = startAt.slice(0, 10);
      if (!date) continue;
      if (!byDate.has(date)) byDate.set(date, []);
      byDate.get(date)!.push(slot);
    }

    const days: AvailabilityResponse[] = Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, daySlots]) => ({
        date,
        slots: daySlots.map(s => ({
          id: String(s.id ?? ''),
          startAt: String(s.startAt ?? ''),
          endAt: String(s.endAt ?? ''),
          durationMinutes: Number(s.durationMinutes ?? 0),
          channel: s.channel as string | undefined,
          // Le mapping ne recopiait que ces cinq champs : `actType`, `price` et `currency`
          // etaient JETES a la frontiere. L'UI affichait donc « — » partout alors que le
          // serveur renvoyait bien 30 € et le nom de l'acte. Un champ du contrat qui
          // n'est pas recopie ici n'existe pas pour l'application.
          actType: (s.actType ?? null) as TimeSlot['actType'],
          price: (s.price ?? null) as number | null,
          currency: (s.currency ?? 'EUR') as string,
        })),
      })) as unknown as AvailabilityResponse[];

    // On renvoie une AxiosResponse-compatible : les appelants lisent `.data`.
    return { ...response, data: days };
  },
  
  /**
   * Get the next available slot for a doctor
   * GET /api/v1/doctors/:id/next-availability
   */
  getNextAvailability: (id: string) => 
    api.get<GetNextAvailabilityResponse>(`/doctors/${id}/next-availability`),
  
  /**
   * Get reviews for a doctor
   * GET /api/v1/reviews/practitioners/:id/reviews
   */
  getReviews: (id: string, page = 0, size = 10) => 
    api.get(`/reviews/practitioners/${id}/reviews`, {
      params: { page, size },
    }),
};

export const slotsApi = {
  /**
   * Tient un créneau pour le patient connecté pendant qu'il finalise sa réservation.
   *
   * POST /api/v1/slots/:slotId/hold
   *
   * <p><b>Pourquoi :</b> le tunnel dure plusieurs minutes (praticien, date, créneau,
   * questionnaire, récapitulatif). Sans verrou, un autre patient prend la place et le premier
   * se voit refuser sa réservation à la dernière étape, après avoir tout saisi.
   *
   * <p>Idempotent : re-tenir son propre créneau prolonge le verrou au lieu d'échouer — un
   * aller-retour entre étapes ne casse donc rien.
   */
  hold: (slotId: string) =>
    api.post<SlotHoldResponse>('/slots/' + slotId + '/hold'),

  /**
   * Libère le verrou du patient connecté (retour arrière, changement de créneau).
   *
   * DELETE /api/v1/slots/:slotId/hold
   *
   * <p>Sans cet appel, la place resterait bloquée pour les autres jusqu'à expiration — cinq
   * minutes de gêne sans raison.
   */
  release: (slotId: string) =>
    api.delete('/slots/' + slotId + '/hold'),
};

/**
 * Verrou de créneau tel que renvoyé par le serveur.
 *
 * <p>`expiresAt` ET `remainingSeconds` sont transmis : l'interface affiche un compte à rebours
 * (« encore 4 min 30 s ») pour que le patient sache qu'il doit finaliser. Sans cette information,
 * un verrou expiré serait découvert par un refus au moment de confirmer.
 */
export interface SlotHoldResponse {
  holdId: string;
  slotId: string;
  expiresAt: string;
  remainingSeconds: number;
  status: string;
}

// ============================================================================
// APPOINTMENTS API
// ============================================================================

export interface CreateAppointmentRequest {
  idempotencyKey: string;
  /**
   * Identifiant du patient — FACULTATIF.
   *
   * <p>Le serveur résout l'identité depuis le token
   * (`createAppointmentForRequester(request, userId)`) : ce champ n'est utile que pour réserver
   * pour un <b>proche</b>, cas où un lien de représentation actif portant le droit `BOOK` est
   * vérifié côté serveur. Le tunnel ne l'envoie plus (ticket S4-05).
   */
  patientId?: string;
  slotId: string;
  source: string;
  preConsultationAnswers?: Record<string, string>;
}

interface CreateAppointmentResponse {
  id: string;
  patientId: string;
  practitionerId: string;
  slotId: string;
  startsAt: string;
  endsAt: string;
  status: string;
  channel: string;
  price: number;
  currency: string;
}

interface CancelAppointmentRequest {
  reason: string;
}

interface RescheduleAppointmentRequest {
  newSlotId: string;
  reason?: string;
}

export const appointmentsApi = {
  /**
   * Create a new appointment (idempotent)
   * POST /api/v1/appointments
   */
  create: (data: CreateAppointmentRequest) => 
    api.post<CreateAppointmentResponse>('/appointments', data, {
      headers: {
        'Idempotency-Key': data.idempotencyKey,
      },
    }),
  
  /**
   * Get appointment details
   * GET /api/v1/appointments/:id
   */
  getDetails: (id: string) => 
    api.get<Appointment>(`/appointments/${id}`),
  
  /**
   * Get all appointments for a patient
   * GET /api/v1/appointments?patientId=:id
   */
  getPatientAppointments: (patientId: string, status?: string, page = 0, size = 10) => 
    api.get(`/appointments`, {
      params: { patientId, status, page, size },
    }),
  
  /**
   * Cancel an appointment
   * POST /api/v1/appointments/:id/cancel
   */
  cancel: (id: string, data: CancelAppointmentRequest) => 
    api.post(`/appointments/${id}/cancel`, data),
  
  /**
   * Reschedule an appointment
   * POST /api/v1/appointments/:id/reschedule
   */
  reschedule: (id: string, data: RescheduleAppointmentRequest) => 
    api.post(`/appointments/${id}/reschedule`, data),
  
  /**
   * Get cancellation policy for an appointment
   * GET /api/v1/appointments/:id/cancellation-policy
   */
  getCancellationPolicy: (id: string) => 
    api.get(`/appointments/${id}/cancellation-policy`),

  /**
   * Exporte le rendez-vous au format iCalendar (.ics).
   *
   * <p>GET /api/v1/appointments/:id/ics — route réelle du contrat (vérifiée dans
   * `/api/v3/api-docs`). Elle renvoie le fichier avec un `Content-Disposition: attachment`,
   * donc le navigateur le télécharge directement ; le nom de fichier est fourni par le serveur
   * (`nomFichier`), pas inventé côté client.
   *
   * <p>Le serveur contrôle l'accès (`getAuthorizedAppointment`) : un rendez-vous d'un autre
   * patient répond 403.
   */
  exportIcs: (id: string) =>
    api.get(`/appointments/${id}/ics`, { responseType: 'blob' }),
};

/**
 * Politique d'annulation d'un praticien — ticket S4-11.
 *
 * <p>GET /api/v1/doctors/:doctorId/cancellation-policy — route réelle, publique comme le reste
 * de l'annuaire. Elle est lisible <b>avant</b> toute réservation, contrairement à
 * `/appointments/:id/cancellation-policy` qui exige un rendez-vous déjà créé.
 *
 * <p>Elle remplace la constante `CANCELLATION_POLICY` qui était codée en dur dans l'étape 7 :
 * un praticien qui facture 40 € d'annulation voyait 25 €.
 */
export interface CancellationPolicyResponse {
  freeCancellationHours: number;
  cancellationFeeAfter: number;
  noShowFee: number;
  currency: string;
  /**
   * `PRATICIEN` = au moins un champ déclaré par le praticien ; `DEFAUT` = valeurs par défaut.
   * Le front doit le dire : présenter un défaut comme un choix du praticien serait faux.
   */
  source: 'PRATICIEN' | 'DEFAUT';
}

export const cancellationPolicyApi = {
  forPractitioner: (practitionerId: string) =>
    api.get<CancellationPolicyResponse>(
      `/doctors/${practitionerId}/cancellation-policy`
    ),
};

// ============================================================================
// INSURANCE API
// ============================================================================

export const insuranceApi = {
  /**
   * Estime le remboursement d'un rendez-vous — SEULE route réellement exposée.
   *
   * <p>GET /api/v1/insurance/estimate?appointmentId=:id
   *
   * <p><b>Code mort supprimé :</b> {@code getProfile} et {@code updateProfile} appelaient
   * {@code GET|PUT /api/v1/me/insurance}, deux routes qui <b>n'existent pas</b> côté serveur
   * (vérifié : {@code 500} en production, et absentes de {@code /api/v3/api-docs}). Leurs propres
   * commentaires disaient « needs to be implemented on the backend ».
   *
   * <p>Elles n'étaient appelées nulle part : les garder entretenait l'illusion que la fonctionnalité
   * existait. Si un jour on veut un profil d'assurance, il faudra créer la route ET le modèle
   * côté serveur — pas seulement ce client.
   */
  estimateReimbursement: (appointmentId: string) =>
    api.get<ReimbursementEstimation>('/insurance/estimate', {
      params: { appointmentId },
    }),
};

// ============================================================================
// WAITLIST API
// ============================================================================

export const waitlistApi = {
  /**
   * Add patient to waitlist for a practitioner
   * POST /api/v1/waitlist
   * Note: This endpoint needs to be implemented on the backend
   */
  join: (data: WaitlistRequest) => 
    api.post('/waitlist', data),
  
  /**
   * Get patient's waitlist entries
   * GET /api/v1/waitlist/me
   * Note: This endpoint needs to be implemented on the backend
   */
  getMyEntries: () => 
    api.get<WaitlistEntry[]>('/waitlist/me'),
  
  /**
   * Remove patient from waitlist
   * DELETE /api/v1/waitlist/:id
   * Note: This endpoint needs to be implemented on the backend
   */
  leave: (id: string) => 
    api.delete(`/waitlist/${id}`),
};

// ============================================================================
// TELECONSULTATION API
// ============================================================================

interface CreateRoomResponse {
  roomId: string;
  joinUrl: string;
  startTime: string;
  endTime: string;
}

interface JoinRoomResponse {
  token: string;
  joinUrl: string;
}

interface SessionStatusResponse {
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  startedAt?: string;
  endedAt?: string;
  durationMinutes?: number;
}

export const teleconsultationApi = {
  /**
   * Create a teleconsultation room
   * POST /api/v1/teleconsultation/:appointmentId/room
   */
  createRoom: (appointmentId: string) => 
    api.post<CreateRoomResponse>(`/teleconsultation/${appointmentId}/room`),
  
  /**
   * Get join token for a teleconsultation room
   * POST /api/v1/teleconsultation/:appointmentId/join
   */
  joinRoom: (appointmentId: string) => 
    api.post<JoinRoomResponse>(`/teleconsultation/${appointmentId}/join`),
  
  /**
   * Get teleconsultation session status
   * GET /api/v1/teleconsultation/:appointmentId/status
   */
  getSessionStatus: (appointmentId: string) => 
    api.get<SessionStatusResponse>(`/teleconsultation/${appointmentId}/status`),
};

// ============================================================================
// PAYMENTS API
// ============================================================================

interface CreatePaymentIntentRequest {
  appointmentId: string;
  currency?: string;
}

interface CreatePaymentIntentResponse {
  clientSecret: string;
  sessionId: string;
}

export const paymentsApi = {
  /**
   * Create a payment intent for an appointment
   * POST /api/v1/payments/create
   */
  createPaymentIntent: (data: CreatePaymentIntentRequest) => 
    api.post<CreatePaymentIntentResponse>('/payments/create', data),
};

// ============================================================================
// EXPORTED AS SINGLE OBJECT FOR CONVENIENCE
// ============================================================================

export const bookingApi = {
  doctors: doctorsApi,
  appointments: appointmentsApi,
  cancellationPolicy: cancellationPolicyApi,
  insurance: insuranceApi,
  waitlist: waitlistApi,
  teleconsultation: teleconsultationApi,
  payments: paymentsApi,
} as const;

export default bookingApi;